import { React } from '../../shared/react.js';
import { Icon } from '../../shared/icons.js';
import { LoadingMark } from '../../shared/ui/LoadingMark.js';
import { useAppRuntime, useAppState } from '../../app/app.context.js';
import { artworkById, effectiveSearchBudget, missedExactIds, recall } from '../../app/app.selectors.js';
import { formatPercent } from '../../shared/utils/format.js';

export function TruthLens() {
  const runtime = useAppRuntime();
  const state = useAppState();
  const measuredRecall = recall(state);
  const missed = missedExactIds(state).size;
  const busy = state.status === 'truth-checking' || state.status === 'probing';
  const topMatch = artworkById(state, state.approximate?.hits[0]?.id ?? null);
  const visualRecipe = state.queryTerms.some((term) => term.kind === 'image' || term.kind === 'detail');

  if (state.status === 'probing') {
    return (
      <section className="truth-lens truth-lens--busy" aria-live="polite">
        <LoadingMark label="Probing search quality"/>
        <div><strong>Finding a query that breaks the index</strong><span>Testing fast search against mathematical ground truth.</span></div>
      </section>
    );
  }

  if (state.status === 'truth-checking') {
    return (
      <section className={`truth-lens truth-lens--busy${visualRecipe ? ' truth-lens--visual' : ''}`} aria-live="polite">
        <LoadingMark label="Running exhaustive search"/>
        <div className={visualRecipe ? 'truth-lens__copy' : undefined}><strong>{visualRecipe ? `Checking ${state.visualIndexProgress.completed.toLocaleString()} visual vectors` : 'Checking every vector'}</strong><span>{visualRecipe ? `Exhaustive top-${state.resultCount} in the ${state.visualIndexProgress.completed.toLocaleString()}-artwork visual index.` : `The exact oracle is building the true top-${state.resultCount}.`}</span></div>
      </section>
    );
  }

  if (state.approximate?.hits.length === 0 && (!state.exact || state.exact.hits.length === 0)) {
    return (
      <section className="truth-lens" aria-label="Truth check">
        <span className="truth-lens__icon"><Icon name="help" size={16}/></span>
        <div className="truth-lens__copy">
          <strong>No matching artworks</strong>
          <span>{state.exact ? 'Exhaustive search found no matches for this query and filters.' : 'Try changing the query or filters, or verify with Truth check.'}</span>
        </div>
        {!state.exact && <button type="button" onClick={() => void runtime.showTruth()}>Truth check <Icon name="arrow-right" size={15}/></button>}
      </section>
    );
  }

  if (!state.exact) {
    return (
      <section className={`truth-lens${visualRecipe ? ' truth-lens--visual' : ''}`} aria-label="Truth check">
        <span className="truth-lens__icon"><Icon name="lightning" size={16}/></span>
        <div className="truth-lens__copy">
          <strong>{topMatch ? `${visualRecipe ? 'Closest available' : 'Top match'} · ${topMatch.title}` : 'Fast answer'}</strong>
          <span>{formatPercent(state.approximate?.inspectedRatio ?? 0, 1)} of {visualRecipe ? `the ${state.visualIndexProgress.completed.toLocaleString()}-artwork visual index` : 'the graph'} inspected · result ready</span>
        </div>
        <button type="button" onClick={() => void runtime.showTruth()} disabled={busy || !state.approximate}>
          Truth check <Icon name="arrow-right" size={15}/>
        </button>
      </section>
    );
  }

  const success = missed === 0;
  return (
    <section className={`truth-lens truth-lens--result ${success ? 'is-success' : 'is-miss'}${visualRecipe ? ' truth-lens--visual' : ''}`} aria-live="polite">
      <span className="truth-lens__icon"><Icon name={success ? 'target' : 'help'} size={16}/></span>
      <div className="truth-lens__copy">
        <strong>{visualRecipe ? `${state.visualIndexProgress.completed.toLocaleString()}-artwork visual index: ${success ? 'all neighbors found' : `${missed} neighbors missed`}` : success ? 'Fast search found every true neighbor' : `Fast search missed ${missed} true ${missed === 1 ? 'neighbor' : 'neighbors'}`}</strong>
        <span>{measuredRecall === null ? 'Truth comparison ready' : `${formatPercent(measuredRecall)} recall${visualRecipe ? ` in the ${state.visualIndexProgress.completed.toLocaleString()}-artwork visual index` : ''} at ef ${effectiveSearchBudget(state)}`}</span>
      </div>
      <div className="truth-lens__switch" role="group" aria-label="Visible result set">
        <button type="button" className={state.resultMode === 'fast' ? 'is-active' : ''} onClick={() => runtime.showFast()}>Fast</button>
        <button type="button" className={state.resultMode === 'truth' ? 'is-active' : ''} onClick={() => void runtime.showTruth()}>Truth</button>
      </div>
    </section>
  );
}
